/**
 * Format Helpers - Montreal Kosher
 * Common formatting utilities used across the app
 */

/**
 * Format a date string for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date (e.g., "Feb 25, 2026")
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format a date string with time
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date/time (e.g., "Feb 25, 2026 3:45 PM")
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

/**
 * Format weight in kilograms
 * @param {number|string} weight - Weight value
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted weight with unit (e.g., "125.50 kg")
 */
export const formatWeight = (weight, decimals = 2) => {
  const num = parseFloat(weight);
  if (isNaN(num)) return '0.00 kg';
  return `${num.toFixed(decimals)} kg`;
};

/**
 * Format weight without unit
 * @param {number|string} weight - Weight value
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted weight number only
 */
export const formatWeightNumber = (weight, decimals = 2) => {
  const num = parseFloat(weight);
  if (isNaN(num)) return '0.00';
  return num.toFixed(decimals);
};

/**
 * Format currency in USD
 * @param {number|string} amount - Currency amount
 * @returns {string} Formatted currency (e.g., "***")
 */
export const formatCurrency = (amount) => {
  const num = parseFloat(amount);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(num);
};

/**
 * Format a number with commas
 * @param {number|string} num - Number to format
 * @returns {string} Formatted number (e.g., "1,234")
 */
export const formatNumber = (num) => {
  const n = parseInt(num, 10);
  if (isNaN(n)) return '0';
  return new Intl.NumberFormat('en-US').format(n);
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Convert status string to CSS class name
 * @param {string} status - Status string (e.g., "pending shipment")
 * @returns {string} CSS class name (e.g., "pending-shipment")
 */
export const statusToClassName = (status) => {
  if (!status) return '';
  return status.replace(/\s+/g, '-').toLowerCase();
};

/**
 * Calculate total weight from an array of items
 * @param {Array} items - Array of items with subtotal_weight property
 * @returns {number} Total weight
 */
export const calculateTotalWeight = (items) => {
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum, item) => sum + (parseFloat(item.subtotal_weight) || 0), 0);
};

/**
 * Calculate total cases/quantity from an array of items
 * @param {Array} items - Array of items with quantity property
 * @returns {number} Total quantity
 */
export const calculateTotalCases = (items) => {
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
};

export default {
  formatDate,
  formatDateTime,
  formatWeight,
  formatWeightNumber,
  formatCurrency,
  formatNumber,
  truncateText,
  statusToClassName,
  calculateTotalWeight,
  calculateTotalCases
};
